'use client';

import { cva, cx } from 'class-variance-authority';
import Link from 'next/link';
import { useState, type ComponentProps, type DOMAttributes, type ReactNode } from 'react';
import Icon, { type IconName } from './Icon';
import { usePortalCtx } from '~/features/global/portal';

export type VariantOptionMap<T = string> = Record<Props['type'], T>;

const variants = {
    title: cva('', {
        variants: {
            type: {
                success: 'text-secondary',
                warning: 'text-yellow-500',
                error: 'text-orange-500',
            } satisfies VariantOptionMap,
        },
        defaultVariants: {
            type: 'success',
        },
    }),
    bg: cva('', {
        variants: {
            type: {
                success: 'bg-secondary-50',
                warning: 'bg-yellow-50',
                error: 'bg-orange-50',
            } satisfies VariantOptionMap,
        },
        defaultVariants: {
            type: 'success',
        },
    }),
};

const ICON_MAP = {
    success: 'thumbs-up',
    warning: 'notification',
    error: 'cross',
} as const satisfies VariantOptionMap<IconName>;

type Props = {
    type: 'success' | 'warning' | 'error';
    title: React.ReactNode;
    description: React.ReactNode;
    /** @default false */
    fill?: boolean;
    className?: string;
    /** @default `arrow-right` */
    buttonIcon?: Icon.Name;
    /** When true, description is collapsible on mobile (toggles on tap) */
    collapsible?: boolean;
} & (
    | {
          href?: ComponentProps<typeof Link>['href'];
          onClick?: never;
      }
    | {
          onClick?: DOMAttributes<HTMLButtonElement>['onClick'];
          href?: never;
      }
);

export default function Callout({
    type,
    title,
    description,
    className,
    buttonIcon = 'arrow-right',
    fill = false,
    collapsible = false,
    ...props
}: Props) {
    const [open, setOpen] = useState(false);
    const portal = usePortalCtx();
    const isBizz = portal.portal === 'BIZZ';

    const collapsibleAction = collapsible ? { onClick: () => setOpen(v => !v) } : undefined;
    const mobileAction = collapsibleAction ?? props;

    const collapsibleIcon = (open === false ? 'chevron-down' : 'chevron-up') as Icon.Name;
    const mobileIcon = collapsible ? collapsibleIcon : buttonIcon;

    return (
        <div
            className={cx(
                'flex rounded-2xl border bg-white p-2',
                isBizz
                    ? 'border-gray-200 shadow-none'
                    : 'border-brown-100 shadow-[0px_2px_0px_0px_#F4EFEA]',
                fill ? 'w-full' : 'w-fit',
                className,
            )}
        >
            <div
                className={variants.bg({
                    type,
                    className: 'hidden rounded-bl-lg rounded-tl-lg px-6 py-8 md:block',
                })}
            >
                <Icon
                    name={ICON_MAP[type]}
                    className={variants.title({ type })}
                    fill={type === 'error' ? 'none' : 'currentColor'}
                    strokeWidth={type === 'error' ? '1.5' : '0'}
                />
            </div>
            <div className="flex w-full">
                <div className={cx('w-full', collapsible && !open ? 'p-4 pb-2' : 'p-4')}>
                    <div
                        className={cx(
                            'flex items-center gap-x-4',
                            collapsible && !open ? 'mb-0' : 'mb-4',
                            'md:mb-0 md:gap-x-0',
                        )}
                    >
                        <div
                            className={variants.bg({
                                type,
                                className: 'rounded-2xl p-3 md:hidden',
                            })}
                        >
                            <Icon
                                name={ICON_MAP[type]}
                                className={variants.title({
                                    type,
                                    className: 'md:block',
                                })}
                                fill={type === 'error' ? 'none' : 'currentColor'}
                                strokeWidth={type === 'error' ? '1.5' : '0'}
                            />
                        </div>
                        <h2
                            className={variants.title({
                                type,
                                className: 'text-base font-bold md:mb-4 md:text-lg',
                            })}
                        >
                            {title}
                        </h2>
                        <ActionComponent
                            className="ml-auto cursor-pointer md:hidden"
                            {...mobileAction}
                        >
                            <Icon
                                name={mobileIcon}
                                className={variants.title({ type })}
                                strokeWidth="2px"
                            />
                        </ActionComponent>
                    </div>
                    {typeof description === 'string' ? (
                        <p
                            className={cx(
                                'text-sm md:text-base',
                                isBizz ? 'text-primary' : 'text-blue-700',
                                collapsible && !open && 'hidden',
                            )}
                        >
                            {description}
                        </p>
                    ) : (
                        <div
                            className={cx(
                                isBizz ? 'text-primary' : 'text-blue-700',
                                collapsible && !open && 'hidden',
                            )}
                        >
                            {description}
                        </div>
                    )}
                </div>

                <ActionComponent
                    className="hidden cursor-pointer items-start justify-center pt-4 pr-6 pl-10 duration-150 hover:scale-110 md:flex"
                    {...(collapsibleAction ?? props)}
                >
                    <Icon
                        name={collapsible ? collapsibleIcon : buttonIcon}
                        className={variants.title({ type })}
                        strokeWidth="2px"
                    />
                </ActionComponent>
            </div>
        </div>
    );
}

// prettier-ignore
const ActionComponent = (
    { className, children, ...props }:
        & { className?: string; children?: ReactNode }
        & Pick<Props, 'href' | 'onClick'>
) => props.href !== undefined
        ? <Link href={props.href} className={className}>{children}</Link>
        : props.onClick
            ? <button type='button' onClick={props.onClick} className={className}>{children}</button>
            : null;
